import React, { FC, useCallback, useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../common/toast/hooks/useToast'
import { Card } from 'primereact/card'
import { api } from '../../../../auth'
import Common from '../../../common/Common'
import { Button, Alert, Row, Col, Form, Tab, Tabs, Badge } from 'react-bootstrap'
import { useAppSelector } from '../../../../store/hooks'
import PageHeader from '../../../layout/page/PageHeader'
import { Formik, Form as FormikForm } from 'formik'
import TextField from '../../../common/forms/fields/TextField'
import TextAreaField from '../../../common/forms/fields/TextAreaField'
import SfyCKEditor from '../../../common/forms/fields/SfyCKEditor'
import { handleAxiosErrors } from '../../../common/forms/errors/Helper'
import RenderFieldsErrors from '../../../common/forms/errors/RenderFieldsErrors'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faCircleNotch,
    faCopy,
    faEnvelope,
    faEye,
    faInfoCircle,
    faPaperPlane,
} from '@fortawesome/free-solid-svg-icons'
import RenderFormikErrors from '../../../common/forms/errors/RenderFormikErrors'
import RecordStatusField from '../../../common/forms/fields/RecordStatusField'
import { HintMessage } from '../../../common/messages/Messages'
import { Language } from '../../../../features/security/security.slice'

interface TemplateVariable {
    key: string
    label: string
}

interface EmailTemplateData {
    id: number
    name: string
    category: string
    description: string | null
    recipient_emails: string | null
    is_active: boolean
    available_variables: TemplateVariable[] | null
    signature_translation_code: string | null
    key?: { code: string; name: string }
    type?: { code: string; name: string }
}

interface EmailTemplateFormProps {
    id?: number | null
    inPopup?: boolean
    config?: any
}

const EmailTemplateForm: FC<EmailTemplateFormProps> = ({
    id = null,
    inPopup = true,
    config = {},
}) => {
    const theme = useAppSelector((state) => state.theme.theme)
    const initData = useAppSelector((state: any) => state.security.initData)
    const navigate = useNavigate()
    const axiosPrivate = api

    const emptyTemplate: EmailTemplateData = {
        id: 0,
        name: '',
        category: '',
        description: '',
        recipient_emails: '',
        is_active: true,
        available_variables: null,
        signature_translation_code: null,
    }

    const [templateDialog, setTemplateDialog] = useState(true)
    const [template, setTemplate] = useState<EmailTemplateData>(emptyTemplate)
    const [initialValues, setInitialValues] = useState<any>({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})
    const [copiedVar, setCopiedVar] = useState<string | null>(null)
    const [previewVisible, setPreviewVisible] = useState(false)
    const [previewHtml, setPreviewHtml] = useState('')
    const [previewSubject, setPreviewSubject] = useState('')
    const [previewLocale, setPreviewLocale] = useState('en')
    const [previewLoading, setPreviewLoading] = useState(false)
    const [sendTestVisible, setSendTestVisible] = useState(false)
    const [sendTestEmail, setSendTestEmail] = useState('')
    const [sendTestLocale, setSendTestLocale] = useState('en')
    const [sendTestLoading, setSendTestLoading] = useState(false)

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        try {
            const response = await axiosPrivate
                .get('/administration/email-templates/' + id)
                .finally(() => setIsLoading(false))

            if (response.data?.success === true) {
                const { template: tpl, translations } = response.data.data
                setTemplate(tpl)

                const formValues: any = {
                    recipient_emails: tpl.recipient_emails || '',
                    is_active: tpl.is_active,
                    description: tpl.description || '',
                }

                if (translations) {
                    Object.keys(translations).forEach((key) => {
                        formValues[key] = translations[key] || ''
                    })
                }

                setInitialValues(formValues)
            } else {
                setErrMsg(response.data?.errors?.join('<br />') || 'Failed to load')
            }
        } catch (err: any) {
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Email template not found')
            } else {
                setErrMsg('Failed to load email template')
            }
        }
    }, [axiosPrivate, id])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async (record: any) => {
        setErrMsg('')
        setIsSubmitting(true)

        try {
            const response = await axiosPrivate
                .post('/administration/email-templates/' + id, record, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                .finally(() => setIsSubmitting(false))

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: config?.submitSuccessMsg?.header || 'Successful',
                    detail: config?.submitSuccessMsg?.message || 'Template saved',
                })
                hideDialog()
            } else {
                setErrMsg(response.data?.errors?.join('<br />') || 'Save failed')
            }
        } catch (err: any) {
            handleAxiosErrors(err, setErrMsg, setErrorFields as any, config as any)
        }
    }

    const hideDialog = () => {
        setTemplateDialog(false)
        navigate('/administration/emails')
    }

    const copyVariable = (varKey: string) => {
        navigator.clipboard.writeText(`[[${varKey}]]`)
        setCopiedVar(varKey)
        setTimeout(() => setCopiedVar(null), 1500)
    }

    const loadPreview = async (locale: string) => {
        setPreviewLoading(true)
        setPreviewLocale(locale)
        try {
            const response = await axiosPrivate.post(
                `/administration/email-templates/preview/${id}`,
                { language: locale }
            )
            if (response.data?.success) {
                setPreviewSubject(response.data.data.subject)
                setPreviewHtml(response.data.data.html)
            }
        } catch {
            triggerToast({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load preview',
            })
        } finally {
            setPreviewLoading(false)
        }
    }

    const openPreview = () => {
        setPreviewVisible(true)
        loadPreview(previewLocale)
    }

    const handleSendTest = async () => {
        if (!sendTestEmail) return
        setSendTestLoading(true)
        try {
            const response = await axiosPrivate.post(
                `/administration/email-templates/send-test/${id}`,
                { email: sendTestEmail, language: sendTestLocale }
            )
            if (response.data?.success) {
                triggerToast({
                    severity: 'success',
                    summary: 'Sent',
                    detail: response.data.data.msg,
                })
                setSendTestVisible(false)
            }
        } catch {
            triggerToast({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to send test email',
            })
        } finally {
            setSendTestLoading(false)
        }
    }

    const formFooter = (
        <React.Fragment>
            <div className="d-flex justify-content-between w-100">
                <div className="d-flex gap-2">
                    <Button
                        variant="outline-info"
                        disabled={isLoading}
                        onClick={openPreview}
                        size="sm"
                    >
                        <FontAwesomeIcon icon={faEye} className="me-1" />
                        <span className="small">Preview</span>
                    </Button>
                    <Button
                        variant="outline-warning"
                        disabled={isLoading}
                        onClick={() => setSendTestVisible(true)}
                        size="sm"
                    >
                        <FontAwesomeIcon
                            icon={faPaperPlane}
                            className="me-1"
                        />
                        <span className="small">Send Test</span>
                    </Button>
                </div>
                <div className="d-flex gap-2">
                    <Button
                        variant={theme === 'dark' ? 'dark' : 'light'}
                        onClick={hideDialog}
                    >
                        <span className="small">Cancel</span>
                    </Button>
                    <Button
                        variant="success"
                        disabled={isSubmitting || isLoading}
                        onClick={() => {
                            document
                                .getElementById(
                                    'email-template-form-submit-btn'
                                )
                                ?.click()
                        }}
                    >
                        <span className="small">
                            {isSubmitting ? (
                                <>
                                    <FontAwesomeIcon
                                        icon={faCircleNotch}
                                        spin={true}
                                    />{' '}
                                    {config?.saveButton?.savingLabel ||
                                        'Saving...'}
                                </>
                            ) : (
                                config?.saveButton?.label || 'Save'
                            )}
                        </span>
                    </Button>
                </div>
            </div>
        </React.Fragment>
    )

    const VariablesPanel = () => {
        if (
            !template.available_variables ||
            template.available_variables.length === 0
        ) {
            return null
        }

        return (
            <fieldset className="mb-3">
                <legend>
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    Available Variables
                    <HintMessage
                        message="Click a variable to copy its placeholder to your clipboard, then paste it into the subject or body."
                    />
                </legend>
                <div className="d-flex flex-wrap gap-2">
                    {template.available_variables.map((v) => (
                        <Button
                            key={v.key}
                            variant={
                                copiedVar === v.key
                                    ? 'success'
                                    : 'outline-secondary'
                            }
                            size="sm"
                            onClick={() => copyVariable(v.key)}
                            title={v.label}
                            className="d-flex align-items-center gap-1"
                        >
                            <FontAwesomeIcon
                                icon={
                                    copiedVar === v.key ? faInfoCircle : faCopy
                                }
                                size="xs"
                            />
                            <code className="text-inherit">
                                {'[[' + v.key + ']]'}
                            </code>
                            {copiedVar === v.key && (
                                <small className="ms-1">Copied!</small>
                            )}
                        </Button>
                    ))}
                </div>
            </fieldset>
        )
    }

    const FormContent = (
        <>
            {isLoading && <Common.Loader text="Loading..." />}
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            {!isLoading && Object.keys(initialValues).length > 0 && (
                <Formik
                    initialValues={initialValues}
                    onSubmit={(values) => saveRecord(values)}
                    enableReinitialize={true}
                >
                    {(formikProps) => (
                        <FormikForm id="email-template-form">
                            <RenderFormikErrors formikProps={formikProps} />

                            <Row>
                                <Col sm={12}>
                                    <RecordStatusField
                                        id="formIsActive"
                                        name="is_active"
                                        onLabel="De-Activate"
                                        offLabel="Activate"
                                        formikProps={formikProps}
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col lg={8}>
                                    <fieldset>
                                        <legend>
                                            <FontAwesomeIcon
                                                icon={faEnvelope}
                                                className="me-2"
                                            />
                                            Template Details
                                        </legend>
                                        <Row>
                                            <Col sm={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="text-muted small">
                                                        Name
                                                    </Form.Label>
                                                    <Form.Control
                                                        plaintext
                                                        readOnly
                                                        value={template.name}
                                                        className="fw-bold"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col sm={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="text-muted small">
                                                        Category
                                                    </Form.Label>
                                                    <div>
                                                        <Badge
                                                            bg={
                                                                template.category ===
                                                                'contact'
                                                                    ? 'primary'
                                                                    : template.category ===
                                                                      'payment'
                                                                    ? 'success'
                                                                    : 'info'
                                                            }
                                                            className="text-capitalize"
                                                        >
                                                            {template.category}
                                                        </Badge>
                                                    </div>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <TextAreaField
                                            id="formDescription"
                                            name="description"
                                            label="Description"
                                            placeholder="Brief description of this template's purpose"
                                            formikProps={formikProps}
                                        />
                                    </fieldset>
                                </Col>

                                <Col lg={4}>
                                    <VariablesPanel />
                                    <fieldset className="mt-3">
                                        <legend>
                                            <FontAwesomeIcon
                                                icon={faPaperPlane}
                                                className="me-2"
                                            />
                                            Target Emails
                                            <HintMessage
                                                message="For admin notification templates (e.g. contact feedback, join requests, payments), enter the email addresses that should receive these emails. Separate multiple addresses with a semicolon (;). Leave empty for user-facing templates."
                                            />
                                        </legend>
                                        <TextField
                                            id="formRecipientEmails"
                                            name="recipient_emails"
                                            label="To (recipient emails)"
                                            placeholder="admin@example.com;team@example.com"
                                            formikProps={formikProps}
                                        />
                                    </fieldset>
                                </Col>
                            </Row>

                            <fieldset className="mt-2">
                                <legend>Email Content</legend>
                                <Tabs
                                    id="emailTemplateLangTabs"
                                    className="mb-0"
                                >
                                    {Object.keys(
                                        initData.languages.data
                                    ).map((locale: string) => {
                                        const lang: Language =
                                            initData.languages.data[locale]

                                        return (
                                            <Tab
                                                eventKey={locale}
                                                title={lang.name}
                                                key={locale}
                                                className="fade border-start border-end border-bottom mb-3 p-3 in-tab-content"
                                            >
                                                <TextField
                                                    id={`formSubject_${lang.id}`}
                                                    name={`subject_${lang.id}`}
                                                    label="Subject"
                                                    placeholder="Email subject line"
                                                    formikProps={formikProps}
                                                    required
                                                />

                                                <Form.Label className="mt-2">
                                                    Body
                                                </Form.Label>
                                                <SfyCKEditor
                                                    id={`formBody_${lang.id}`}
                                                    name={`body_${lang.id}`}
                                                    formikProps={formikProps}
                                                    placeholder="Email body content"
                                                />

                                                {template.signature_translation_code && (
                                                    <>
                                                        <Form.Label className="mt-3">
                                                            Signature
                                                        </Form.Label>
                                                        <SfyCKEditor
                                                            id={`formSignature_${lang.id}`}
                                                            name={`signature_${lang.id}`}
                                                            formikProps={
                                                                formikProps
                                                            }
                                                            placeholder="Email signature"
                                                        />
                                                    </>
                                                )}
                                            </Tab>
                                        )
                                    })}
                                </Tabs>
                            </fieldset>

                            <input
                                type="submit"
                                id="email-template-form-submit-btn"
                                className="d-none"
                            />
                        </FormikForm>
                    )}
                </Formik>
            )}
        </>
    )

    const previewModalHeader = (
        <div className="d-flex align-items-center gap-3 w-100">
            <span>Email Preview</span>
            <div className="d-flex gap-1">
                {initData?.languages?.data &&
                    Object.keys(initData.languages.data).map(
                        (locale: string) => (
                            <Button
                                key={locale}
                                variant={
                                    previewLocale === locale
                                        ? 'primary'
                                        : 'outline-secondary'
                                }
                                size="sm"
                                onClick={() => loadPreview(locale)}
                                disabled={previewLoading}
                            >
                                {initData.languages.data[locale].name}
                            </Button>
                        )
                    )}
            </div>
        </div>
    )

    return (
        <>
            {inPopup ? (
                <Card className="flex justify-content-center">
                    <Dialog
                        visible={templateDialog}
                        breakpoints={{ '960px': '100vw', '640px': '100vw' }}
                        style={{ width: '80vw' }}
                        header={
                            isLoading
                                ? 'Loading...'
                                : `Edit: ${template.name}`
                        }
                        modal
                        className="p-fluid"
                        footer={formFooter}
                        onHide={hideDialog}
                        maximizable
                        contentClassName={isLoading ? 'wrapped-content' : ''}
                        closeOnEscape={false}
                    >
                        {FormContent}
                    </Dialog>
                </Card>
            ) : (
                <>
                    <PageHeader
                        header={
                            isLoading
                                ? 'Loading...'
                                : `Edit: ${template.name}`
                        }
                    />
                    {FormContent}
                    {formFooter}
                </>
            )}

            <Dialog
                visible={previewVisible}
                onHide={() => setPreviewVisible(false)}
                header={previewModalHeader}
                style={{ width: '85vw' }}
                breakpoints={{ '960px': '100vw' }}
                modal
                maximizable
            >
                {previewLoading ? (
                    <Common.Loader text="Rendering preview..." />
                ) : (
                    <>
                        <div className="mb-3 p-2 border-bottom">
                            <strong className="text-muted small me-2">
                                Subject:
                            </strong>
                            <span>{previewSubject}</span>
                        </div>
                        <iframe
                            srcDoc={previewHtml}
                            title="Email Preview"
                            style={{
                                width: '100%',
                                height: '70vh',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                background: '#fff',
                            }}
                        />
                    </>
                )}
            </Dialog>

            <Dialog
                visible={sendTestVisible}
                onHide={() => setSendTestVisible(false)}
                header="Send Test Email"
                style={{ width: '450px' }}
                modal
                footer={
                    <div className="d-flex justify-content-end gap-2">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setSendTestVisible(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            disabled={sendTestLoading || !sendTestEmail}
                            onClick={handleSendTest}
                        >
                            {sendTestLoading ? (
                                <>
                                    <FontAwesomeIcon
                                        icon={faCircleNotch}
                                        spin
                                    />{' '}
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon
                                        icon={faPaperPlane}
                                        className="me-1"
                                    />{' '}
                                    Send
                                </>
                            )}
                        </Button>
                    </div>
                }
            >
                <Form.Group className="mb-3">
                    <Form.Label>Recipient Email</Form.Label>
                    <Form.Control
                        type="email"
                        placeholder="test@example.com"
                        value={sendTestEmail}
                        onChange={(e) => setSendTestEmail(e.target.value)}
                        autoFocus
                    />
                    <Form.Text className="text-muted">
                        The test email will be sent to this address with sample
                        data.
                    </Form.Text>
                </Form.Group>
                <Form.Group>
                    <Form.Label>Language</Form.Label>
                    <div className="d-flex gap-2">
                        {initData?.languages?.data &&
                            Object.keys(initData.languages.data).map(
                                (locale: string) => (
                                    <Button
                                        key={locale}
                                        variant={
                                            sendTestLocale === locale
                                                ? 'primary'
                                                : 'outline-secondary'
                                        }
                                        size="sm"
                                        onClick={() =>
                                            setSendTestLocale(locale)
                                        }
                                    >
                                        {initData.languages.data[locale].name}
                                    </Button>
                                )
                            )}
                    </div>
                </Form.Group>
            </Dialog>
        </>
    )
}

export default EmailTemplateForm
